import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Back to login</span>
        </Link>

        <div className="bg-zinc-900/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-500" weight="fill" />
              </div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif] mb-2">Check your email</h1>
              <p className="text-zinc-400 text-sm mb-6">
                If an account exists for {email}, you'll receive a password reset link shortly.
              </p>
              <Link 
                to="/login" 
                className="text-white hover:underline text-sm"
              >
                Return to login
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mx-auto mb-4">
                  <span className="text-black font-bold text-lg">BF</span>
                </div>
                <h1 className="text-2xl font-medium font-['Instrument_Serif',serif] mb-2">Forgot password?</h1>
                <p className="text-zinc-400 text-sm">Enter your email and we'll send you a reset link</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
                  <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    placeholder="you@example.com"
                    required
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30 transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                  className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-zinc-400 text-sm">
                  Remember your password?{' '}
                  <Link to="/login" className="text-white hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
